/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, tap } from 'rxjs';
import { PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-copy-term-config-dialog',
  templateUrl: './copy-term-config.dialog.html',
  standalone: false,
})
export class CopyTermConfigDialog {
  constructor(
    private formBuilder: FormBuilder,
    private policySvc: PolicyService
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  @PropertyListener('selectedTermId') selectedTermId$ = new BehaviorSubject<string | null>(null);
  @Input() selectedTermId: string | null = null;

  @PropertyListener('coverageConfigs') coverageConfigs$ = new BehaviorSubject<Policy.Plan.PlanCoverageConfigs | null>(
    null
  );
  @Input() coverageConfigs: Policy.Plan.PlanCoverageConfigs | null = null;

  @Output() closed = new EventEmitter();

  @Output() configure = new EventEmitter<{
    configs?: Policy.Plan.CoverageConfig[];
    fees?: Policy.PlanCoverageFee.Model[];
    done?: () => void;
    error?: (err: any) => void;
  }>();

  plan: Policy.Plan.Model | null = null;
  // optional cache of fees per term provided by parent (set dynamically)
  termFeesCache: Record<string, Policy.PlanCoverageFee.Model[] | undefined> = {};

  completedCount = 0;

  form = this.formBuilder.group({
    termId: [null as string | null, Validators.required],
  });

  loading = false;
  errorMsg: string | null = null;

  completedTerms$ = combineLatest([this.policy$, this.coverageConfigs$, this.selectedTermId$]).pipe(
    map(([policy, coverageConfigs, selectedTermId]) => {
      if (!policy || !coverageConfigs) return [];

      const completedTerms: Policy.PolicyTerm[] = [];

      for (const term of policy.terms) {
        const configs = coverageConfigs[term.id];
        if (!configs || term.id === selectedTermId) continue;
        let completed = true;

        if (configs.length) {
          for (const config of configs) {
            if (
              !config.deductible ||
              ((config.deductible.defaultValue === null || config.deductible.defaultValue === undefined) &&
                !config.deductible.valuePropertyPath)
            ) {
              completed = false;
              break;
            }

            if (
              !config.liabilityLimit ||
              ((config.liabilityLimit.defaultValue === null || config.liabilityLimit.defaultValue === undefined) &&
                !config.liabilityLimit.valuePropertyPath)
            ) {
              completed = false;
              break;
            }

            if (
              !config.liabilityGroups ||
              ((config.liabilityGroups.defaultValue === null || config.liabilityGroups.defaultValue === undefined) &&
                !config.liabilityGroups.valuePropertyPath)
            ) {
              completed = false;
              break;
            }

            if (
              !config.requirement ||
              ((config.requirement.defaultValue === null || config.requirement.defaultValue === undefined) &&
                !config.requirement.valuePropertyPath)
            ) {
              completed = false;
              break;
            }

            if (
              term.paymentSchedules.length &&
              (!config.price ||
                ((config.price.defaultValue === null || config.price.defaultValue === undefined) &&
                  !config.price.valuePropertyPath))
            ) {
              completed = false;
              break;
            }
          }
        } else {
          completed = false;
        }

        if (completed) completedTerms.push(term);
      }

      return completedTerms;
    }),
    tap(completed => (this.completedCount = completed.length))
  );

  open(plan: Policy.Plan.Model) {
    this.plan = plan;
    // capture any injected cache attached by parent
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.termFeesCache = (plan as any).__termFeesCache ?? {};
    this.form.reset({ termId: null });
    this.loading = false;
    this.errorMsg = null;
    if (this.completedCount) {
      this.dialog.open();
    } else {
      //start from scratch
      this.submit();
    }
  }

  close() {
    this.form.reset({ termId: null });
    this.loading = false;
    this.errorMsg = null;
    this.closed.emit();
    this.dialog.close();
  }

  onConfigureComplete() {
    this.close();
  }

  onConfigureError(err: any) {
    this.loading = false;
    this.errorMsg = err?.message || 'An error occurred.';
  }

  async submit(configs?: Policy.Plan.CoverageConfig[], fees?: Policy.PlanCoverageFee.Model[]) {
    this.loading = true;
    this.errorMsg = null;
    // Parent must call onConfigureComplete/onConfigureError
    this.configure.emit({
      configs: configs?.length ? [...configs] : undefined,
      fees: fees?.length ? [...fees] : undefined,
      done: () => this.onConfigureComplete(),
      error: (err: any) => this.onConfigureError(err),
    });
  }

  async submitWithTermConfig() {
    if (!this.selectedTermId) return;
    const termId = this.form.value.termId;
    if (!termId || !this.coverageConfigs) return;
    this.loading = true;
    this.errorMsg = null;
    try {
      const coverageConfigs = this.coverageConfigs[termId];
      const feesToSelect: Policy.PlanCoverageFee.Model[] = [];

      const cachedFees = this.termFeesCache[termId];
      if (cachedFees?.length) {
        feesToSelect.push(...cachedFees);
      } else if (this.plan) {
        const fees = await this.fetchAllFees(termId);
        if (fees.length) {
          feesToSelect.push(...fees);
          this.termFeesCache[termId] = fees;
        }
      }
      await this.submit(coverageConfigs ?? undefined, feesToSelect);
    } catch (err) {
      this.loading = false;
      this.errorMsg =
        err && typeof err === 'object' && 'message' in err
          ? (err as any).message
          : typeof err === 'string'
            ? err
            : 'An error occurred.';
    }
  }

  /**
   * Fetch all fees for a term, paging until the backend reports no more pages.
   * Limits each page to 100 items to match API defaults.
   */
  private async fetchAllFees(termId: string): Promise<Policy.PlanCoverageFee.Model[]> {
    if (!this.plan) return [];

    const all: Policy.PlanCoverageFee.Model[] = [];
    const limit = 100;
    let page = 1;
    // safety cap to avoid runaway loops if meta is missing
    const maxPages = 20;

    while (page <= maxPages) {
      const res = await this.policySvc.getPlanFees(this.plan.policyId, this.plan.id, termId, { page, limit });
      const items = res?.items ?? [];
      if (!items.length) break;
      all.push(...items);

      const totalPages = res?.meta?.totalPages ?? (items.length < limit ? page : undefined);
      if (!totalPages || page >= totalPages || items.length < limit) break;
      page += 1;
    }

    return all;
  }
}
